import { type TimeValue } from "@via/components/time-picker/time-picker.tsx";

export const MAX_MESSAGE_LENGTH = 100;

/**
 * Extract date string from DatePicker onChange value.
 * Handles both string and object formats returned by the DatePicker component.
 */
export const extractDateFromPicker = (value: unknown): string => {
  if (value && typeof value === "string") {
    return value;
  }
  if (
    value &&
    typeof value === "object" &&
    "startDate" in value &&
    (value as { startDate: unknown }).startDate
  ) {
    return (value as { startDate: unknown }).startDate?.toString() ?? "";
  }
  return "";
};

// Helper to format date for DatePicker (YYYY-MM-DD) using local time
export const formatDateForPicker = (isoString: string | undefined): string => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper to extract time components from ISO string
export const parseTimeFromISO = (isoString: string | undefined): TimeValue => {
  if (!isoString) return { hour: "12", minute: "00", ampm: "AM" };
  const date = new Date(isoString);
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm: "AM" | "PM" = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const roundedMinutes = Math.round(minutes / 15) * 15;
  return {
    hour: hours.toString().padStart(2, "0"),
    minute: (roundedMinutes % 60).toString().padStart(2, "0"),
    ampm,
  };
};

// Helper to combine date and time components into ISO string
export const combineDateAndTime = (
  dateStr: string,
  hour: string,
  minute: string,
  ampm: "AM" | "PM"
): string => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-").map(Number);
  let hours24 = parseInt(hour, 10);
  if (ampm === "PM" && hours24 !== 12) {
    hours24 += 12;
  } else if (ampm === "AM" && hours24 === 12) {
    hours24 = 0;
  }
  const date = new Date(
    year,
    month - 1,
    day,
    hours24,
    parseInt(minute, 10),
    0,
    0
  );
  return date.toISOString();
};

export const parseLocalDateFromYmd = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

export const isEndAfterStart = (
  startDate: string,
  startTime: TimeValue,
  endDate: string,
  endTime: TimeValue
): boolean => {
  const startDatetime = combineDateAndTime(
    startDate,
    startTime.hour,
    startTime.minute,
    startTime.ampm
  );
  const endDatetime = combineDateAndTime(
    endDate,
    endTime.hour,
    endTime.minute,
    endTime.ampm
  );
  if (!startDatetime || !endDatetime) return false;
  return new Date(endDatetime).getTime() > new Date(startDatetime).getTime();
};

// Format datetime for display
export const formatDateTime = (
  isoString: string | null | undefined
): string => {
  if (!isoString) return "-";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return isoString;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * Safely gets the user's timezone identifier.
 * Returns a fallback value if timezone cannot be determined.
 */
export const getUserTimezone = (): string => {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timeZone || "local timezone";
  } catch {
    return "local timezone";
  }
};
